import { Dispatch, SetStateAction, createContext, useContext } from 'react'

import { useRouter } from 'next/router'

import { CustomMarkerOption } from '@web/components/Map/New/types'
import { TimelineTab, TimelineType } from '@web/federation/gql/graphql'
import { useNavigation } from '@web/hooks/useNavigation'
import { voidCallback } from '@web/libs/constants'
import { mergeQueryParams } from '@web/libs/url'
import { TIMELINE_PAGE_SIZE } from '@web/pages/timeline/constant'
import { Nullish } from '@web/types'

export type BottomSheetViewMode = 'full' | 'half' | 'mini'

export const DATE_TIME_FILTER_TOKEN = 'YYYY-MM-DD'

export interface TimelineContextType {
  tab: TimelineTab
  dateRangeFrom: Nullish<string>
  dateRangeTo: Nullish<string>
  category: Nullish<string>
  orderType: Nullish<TimelineType>
  pageSize: number
  userIdno?: string | undefined
  goToTimelineEnd?: (id: string) => void
  backToTimelineList?: () => void
  debug: boolean
  setDisplayMarkers: Dispatch<SetStateAction<CustomMarkerOption[]>>
  setTimelineLoading: Dispatch<SetStateAction<boolean>>
  setTimelineMarkers: Dispatch<SetStateAction<CustomMarkerOption[]>>
  setUpcomingLoading: Dispatch<SetStateAction<boolean>>
  setUpcomingMarkers: Dispatch<SetStateAction<CustomMarkerOption[]>>
  setViewSegmentIndex: Dispatch<SetStateAction<number>>
  displayMarkers: CustomMarkerOption[]
  mapSegmentIndex: number
  viewMode: BottomSheetViewMode
}

export type TimelineFilterType = Partial<
  Pick<TimelineContextType, 'tab' | 'dateRangeFrom' | 'dateRangeTo' | 'category' | 'orderType'>
>

export type TimelinePagePropsContextType = Pick<
  TimelineContextType,
  'userIdno' | 'pageSize' | 'tab' | 'dateRangeFrom' | 'dateRangeTo' | 'category' | 'orderType'
>

export type PeriodInputType = {
  ago: number
  unit: 'month' | 'year' | 'day'
}

export const TimelineContext = createContext<TimelineContextType>({
  tab: TimelineTab.All,
  dateRangeFrom: null,
  dateRangeTo: null,
  category: null,
  orderType: null,
  pageSize: TIMELINE_PAGE_SIZE,
  debug: false,
  setDisplayMarkers: voidCallback,
  setTimelineMarkers: voidCallback,
  setUpcomingMarkers: voidCallback,
  setViewSegmentIndex: voidCallback,
  setUpcomingLoading: voidCallback,
  setTimelineLoading: voidCallback,
  displayMarkers: [],
  mapSegmentIndex: 0,
  viewMode: 'half',
})

export function useTimelineContext() {
  return useContext(TimelineContext)
}

export const useApplyFilter = () => {
  const navigation = useNavigation()
  const router = useRouter()

  /**
   * 설정한 필터를 바탕으로 replace 로 주소 이동을 합니다.
   * 추가 설정할 필터만 전달해야합니다!
   * e.g. tab 만 들어올시 탭이동으로 판단하고 다른 필터 초기화 로직이 들어가있음
   * @param filter
   */
  const applyFilter = (filter: TimelineFilterType) => {
    navigation.push({
      url: router.pathname,
      query: filter.tab ? { tab: filter.tab } : mergeQueryParams(router.query, filter),
      replace: true,
    })
  }

  return applyFilter
}
